/* eslint-disable jsx-a11y/tabindex-no-positive */

import React, { useEffect, useState } from "react";
import { useRouteData } from "react-static";
import { isIOS, useCollected } from "@utils/navigation";
import {
  getInteractivityState,
  InteractivityState,
  loadFramework,
  startInteractivity,
  tryStartVideo
} from "@utils/interactivity";
import { useAppDispatch } from "@components/PersistedData/hooks";
import PopupAllCollected from "@components/PopupAllCollected";
import { Loading } from "../components/TransitionRoot";
import PlaceAlternate from "../components/PlaceAlternate";
import { useAccessibility } from "../components/Accessibility";
import Layout from "../components/Layout";
import { useI18n } from "../I18nextProvider";
import { setMode, setShow360Warning, useCurrentSettings } from "../components/PersistedData/settingsSlice";
import useBodyClass from "../utils/useBodyClass";

const savedCollectedCount = {};

export default () => {
  const i18n = useI18n();
  const { sub, item: place } = useRouteData();
  const settings = useCurrentSettings();
  const collectedCinematic = useCollected("cinematic");
  const collectedObjects = useCollected("collectable");
  const collectedPlaces = useCollected("place");
  const collectedPoints = place.interactPoints.filter((i) => {
    return collectedObjects.indexOf(i.id) >= 0;
  });
  const realCollectedCount = collectedPoints.length;
  const [allCollectedPopup, setAllCollectedPopup] = useState<boolean>(false);
  // Nécessaire pour éviter des crash sur iOS
  const [ready, setReady] = useState(false);
  const [collectedCount, setCollectedCount] = useState<number>();
  const nextCollected = collectedPlaces.indexOf(place?.next?.id) >= 0;
  const [loading, setLoading] = useState(settings.mode === "full");

  const dispatch = useAppDispatch();

  useBodyClass("placeview", "");

  useEffect(() => {
    const pId = place?.id;
    if (pId) {
      const nCol =
        typeof savedCollectedCount[place.id] === "number" ? savedCollectedCount[place?.id] : realCollectedCount;
      if (settings.mode !== "full") {
        if (realCollectedCount !== nCol) {
          setNbCollected(realCollectedCount);
        }
      } else {
        setCollectedCount(nCol);
      }
      if (nCol === place.interactPoints.length && !nextCollected && place?.next?.id) {
        setAllCollectedPopup(true);
      }
      if (place.cinematicApres) {
        const videoApresCollected = collectedCinematic.indexOf(place?.cinematicApres) >= 0;
        if (nCol === place.interactPoints.length && place.cinematicApres && !videoApresCollected) {
          setAllCollectedPopup(true);
        }
      }
      return () => {
        setNbCollected(realCollectedCount);
      };
    }
  }, [place?.id, realCollectedCount, settings.mode]);

  useEffect(() => {
    async function aload() {
      await loadFramework();
      if (!settings.show360Warning) {
        await tryStartVideo();
        setReady(true);
      }
      setLoading(false);
    }
    aload();
  }, []);

  function setNbCollected(qt: number) {
    const old = savedCollectedCount[place.id];
    if (old !== qt) {
      savedCollectedCount[place.id] = qt;
      setCollectedCount(qt);
      if (qt === place.interactPoints.length) {
        if (old || (!nextCollected && place?.next?.id)) {
          setAllCollectedPopup(true);
        }
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  const onKeySelection = (e: React.KeyboardEvent, f: Function) => {
    // Accessibilité, trigger le onClick avec le clavier aussi
    if (e.key === "Enter") {
      f();
    }
  };

  const onFullModeClick = async () => {
    await startInteractivity();
    setReady(true);
    dispatch(setShow360Warning(false));
  };

  const onBasicModeClick = () => {
    dispatch(setMode("basic"));
  };

  let popup = null;
  let enableAltBtn = false;
  if (allCollectedPopup) {
    popup = <PopupAllCollected place={place} />;
  } else if (settings.mode === "full" && !ready) {
    popup = (
      <main className="place directaccess" id="maincontent" tabIndex={-1}>
        <div className="wrapper">
          <h2>{i18n.t("place.directaccess.title")}</h2>

          <a
            onClick={onFullModeClick}
            onKeyDown={(e) => onKeySelection(e, onFullModeClick)}
            tabIndex={1}
            className="bouton-or"
            role="button"
          >
            <span>
              {i18n.t("place.directaccess.full")}
              <br />
              <small>({i18n.t("settings.mode_full")})</small>
            </span>
          </a>
          <a
            onClick={onBasicModeClick}
            onKeyDown={(e) => onKeySelection(e, onBasicModeClick)}
            tabIndex={1}
            className="bouton-or"
            role="button"
          >
            <span>
              {i18n.t("place.directaccess.basic")}
              <br />
              <small>({i18n.t("settings.mode_basic")})</small>
            </span>
          </a>
          <p>{i18n.t("place.directaccess.notice")}</p>
        </div>
      </main>
    );
    if (loading) {
      popup = <Loading />;
    }
  } else {
    enableAltBtn = settings.mode === "full";
  }

  const access = useAccessibility({
    full: ready ? (
      [
        () => import("../components/Place360"),
        // Key prevent react from trying to keep this components between navigations
        { place, collectedCount, setNbCollected, key: place.id, mainContentOnPopup: !!popup }
      ]
    ) : (
      <Loading />
    ),
    noscript: <PlaceAlternate place={place} sub={sub} mainContentOnPopup={!!popup} />
  });

  return (
    <>
      {enableAltBtn && (
        <button type="button" className="skip-main" onClick={onBasicModeClick} tabIndex={0}>
          {i18n.t("place.btn_altversion")}
        </button>
      )}
      <Layout recordLocation>
        {popup}

        <div tabIndex={-1}>{!allCollectedPopup && access}</div>
      </Layout>
    </>
  );
};
